import { useCallback, useMemo, useState } from 'react';
import { groupsService } from '@wlb/shared/api/groups';
import type { Group, GroupSortOption, GroupTypeFilter } from '@wlb/shared/types';
import useDebounce from '@wlb/shared/hooks/useDebounce';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Avatar } from '../components/ui/Avatar';
import { DataTable, type DataTableColumn } from '../components/ui/DataTable';
import { IconButton } from '../components/ui/IconButton';
import { PageHeader } from '../components/layout/PageHeader';
import { Pagination } from '../components/ui/Pagination';
import { SelectField } from '../components/ui/SelectField';
import { StatusPill } from '../components/ui/StatusPill';
import { TextField } from '../components/ui/TextField';
import { GroupDetailsDialog } from '../features/groups/GroupDetailsDialog';
import { useAsyncResource } from '../hooks/useAsyncResource';
import { formatCount, formatDate } from '../utils/format';
import { resolveImageUrl } from '../utils/resolveImageUrl';
import '../components/ui/ui.css';
import './pages.css';

const PAGE_SIZE = 20;

const PRIVACY_FILTERS: readonly { value: GroupTypeFilter; labelKey: string }[] = [
  { value: 'all', labelKey: 'groups.filters.privacy.all' },
  { value: 'public', labelKey: 'groups.filters.privacy.public' },
  { value: 'private', labelKey: 'groups.filters.privacy.private' },
];

const SORT_OPTIONS: readonly { value: GroupSortOption; labelKey: string }[] = [
  { value: 'newest', labelKey: 'groups.filters.sort.newest' },
  { value: 'oldest', labelKey: 'groups.filters.sort.oldest' },
  { value: 'members', labelKey: 'groups.filters.sort.members' },
  { value: 'alpha_asc', labelKey: 'groups.filters.sort.alpha_asc' },
  { value: 'alpha_desc', labelKey: 'groups.filters.sort.alpha_desc' },
];

/**
 * Groups has no admin-specific endpoint: the panel reads the same
 * `/api/v1/Groups/GetGroups` list the member apps use, through the shared
 * groups service, so the mapping stays in one place.
 */
export function Groups() {
  const { t } = useTranslation('admin');
  const [search, setSearch] = useState('');
  const [privacyFilter, setPrivacyFilter] = useState<GroupTypeFilter>('all');
  const [sortBy, setSortBy] = useState<GroupSortOption>('newest');
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const debouncedSearch = useDebounce(search);

  const load = useCallback(
    () =>
      groupsService.getAllGroups({
        searchTerm: debouncedSearch.trim() || undefined,
        pageNumber,
        pageSize: PAGE_SIZE,
        sortBy,
        groupType: privacyFilter,
      }),
    [debouncedSearch, pageNumber, privacyFilter, sortBy],
  );

  const requestKey = [debouncedSearch, privacyFilter, sortBy, pageNumber].join('|');

  const { data, status, refreshing, error, reload } = useAsyncResource(
    load,
    requestKey,
    t('common.errorTitle'),
    true,
    'groups',
  );

  const columns = useMemo<readonly DataTableColumn<Group>[]>(
    () => [
      {
        key: 'name',
        header: t('groups.columns.name'),
        render: group => (
          <div className="ui-table__cell-with-avatar">
            <Avatar
              src={resolveImageUrl(group.groupImage)}
              name={group.groupName}
            />
            <span>
              <span className="ui-table__primary">{group.groupName}</span>
              {group.groupDescription ? (
                <span className="ui-table__secondary ui-table__truncate">
                  {group.groupDescription}
                </span>
              ) : null}
            </span>
          </div>
        ),
      },
      {
        key: 'privacy',
        header: t('groups.columns.privacy'),
        render: group => (
          <StatusPill
            label={t(
              group.privacyType === 'private' ? 'groups.privacy.private' : 'groups.privacy.public',
            )}
            tone={group.privacyType === 'private' ? 'warning' : 'info'}
          />
        ),
      },
      {
        key: 'members',
        header: t('groups.columns.members'),
        numeric: true,
        render: group => formatCount(group.totalMembers),
      },
      {
        key: 'created',
        header: t('groups.columns.created'),
        render: group => formatDate(group.createdAt) || t('common.none'),
      },
      {
        key: 'lastPost',
        header: t('groups.columns.lastPost'),
        render: group => formatDate(group.lastPostDate) || t('common.none'),
      },
      {
        key: 'status',
        header: t('groups.columns.status'),
        render: group => {
          if (group.isDeleted === true) {
            return <StatusPill label={t('status.deleted')} tone="danger" />;
          }
          if (group.isActive === false) {
            return <StatusPill label={t('status.inactive')} tone="neutral" />;
          }
          return <StatusPill label={t('status.active')} tone="success" />;
        },
      },
      {
        key: 'actions',
        header: t('common.actions'),
        render: group => (
          <div className="ui-table__actions">
            <IconButton
              icon="settings"
              tone="slate"
              label={t('groups.actions.manage')}
              onClick={() => setSelectedGroup(group)}
            />
          </div>
        ),
      },
    ],
    [t],
  );

  const rows = data?.data ?? [];

  return (
    <div className="page page--fill">
      <PageHeader
        title={t('groups.title')}
        subtitle={t('groups.subtitle')}
        actions={
          <IconButton
            icon="refresh"
            tone="slate"
            label={t('common.refresh')}
            disabled={refreshing}
            onClick={reload}
          />
        }
      />

      <section className="page__toolbar" aria-label={t('common.filters')}>
        <div className="page__toolbar-field page__toolbar-field--wide">
          <TextField
            label={t('common.search')}
            type="search"
            placeholder={t('groups.searchPlaceholder')}
            value={search}
            onChange={event => {
              setPageNumber(1);
              setSearch(event.target.value);
            }}
          />
        </div>
        <div className="page__toolbar-field">
          <SelectField
            label={t('groups.filters.privacyLabel')}
            value={privacyFilter}
            options={PRIVACY_FILTERS.map(option => ({
              value: option.value,
              label: t(option.labelKey),
            }))}
            onChange={event => {
              setPageNumber(1);
              setPrivacyFilter(event.target.value as GroupTypeFilter);
            }}
          />
        </div>
        <div className="page__toolbar-field">
          <SelectField
            label={t('groups.filters.sortLabel')}
            value={sortBy}
            options={SORT_OPTIONS.map(option => ({
              value: option.value,
              label: t(option.labelKey),
            }))}
            onChange={event => {
              setPageNumber(1);
              setSortBy(event.target.value as GroupSortOption);
            }}
          />
        </div>
      </section>

      <DataTable
        caption={t('groups.title')}
        columns={columns}
        rows={rows}
        rowKey={group => group.id}
        status={status}
        refreshing={refreshing}
        error={error}
        onRetry={reload}
        emptyTitle={t('groups.emptyTitle')}
        emptyMessage={t('groups.emptyMessage')}
        scrollResetKey={requestKey}
        footer={
          data ? (
            <Pagination
              pageNumber={data.pageNumber}
              totalPages={data.totalPages}
              totalCount={data.totalCount}
              hasPreviousPage={data.hasPreviousPage ?? data.pageNumber > 1}
              hasNextPage={data.hasNextPage ?? data.pageNumber < data.totalPages}
              pageSize={PAGE_SIZE}
              onChange={setPageNumber}
            />
          ) : null
        }
      />

      {selectedGroup ? (
        <GroupDetailsDialog
          groupId={selectedGroup.id}
          groupName={selectedGroup.groupName}
          onClose={() => setSelectedGroup(null)}
          onChanged={reload}
        />
      ) : null}
    </div>
  );
}
